import { useState, useEffect, useRef } from 'react'
import Header, { SystemStatus } from './components/Header'
import Home from './pages/Home'
import AuthDialog from './components/AuthDialog'
import NotificationModal from './components/NotificationModal'
import PriceAlertModal from './components/PriceAlertModal'
import type { AuthUser } from './types/auth'

interface StatsResponse {
  total_products: number
  available_products: number
  categories: Record<string, number>
  last_scrape_time: string | number | null
  total_subscriptions: number
  scraper_status?: SystemStatus
}

function App() {
  const contentRef = useRef<HTMLDivElement | null>(null)
  const [filteredCount, setFilteredCount] = useState<number>(0)
  const [isNotificationOpen, setIsNotificationOpen] = useState(false)
  const [isPriceAlertOpen, setIsPriceAlertOpen] = useState(false)
  const [isAuthDialogOpen, setIsAuthDialogOpen] = useState(false)
  const [categories, setCategories] = useState<string[]>([])
  const [systemStatus, setSystemStatus] = useState<SystemStatus | undefined>()
  const [authUser, setAuthUser] = useState<AuthUser | null>(null)
  const [authLoading, setAuthLoading] = useState(true)
  const [authTarget, setAuthTarget] = useState<'notifications' | 'price-alerts' | null>(null)
  const hasOpenOverlay =
    isAuthDialogOpen || isNotificationOpen || isPriceAlertOpen

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await fetch('/api/stats')
        const data: StatsResponse = await res.json()
        if (data.scraper_status) {
          setSystemStatus(data.scraper_status)
        }
      } catch (err) {
        console.error('Failed to fetch stats:', err)
      }
    }

    fetchStatus()
    const interval = setInterval(fetchStatus, 60000) // Every minute
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const fetchSession = async () => {
      try {
        const res = await fetch('/api/auth/session')
        const data = await res.json()
        setAuthUser(data.user || null)
      } catch (err) {
        console.error('Failed to fetch auth session:', err)
        setAuthUser(null)
      } finally {
        setAuthLoading(false)
      }
    }

    fetchSession()
  }, [])

  useEffect(() => {
    if (authLoading || authUser) {
      return
    }
    setIsNotificationOpen(false)
    setIsPriceAlertOpen(false)
  }, [authUser, authLoading])

  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    if (hasOpenOverlay) {
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [hasOpenOverlay])

  useEffect(() => {
    const contentNode = contentRef.current
    if (!contentNode) {
      return
    }

    if (hasOpenOverlay) {
      contentNode.setAttribute('inert', '')
      return () => {
        contentNode.removeAttribute('inert')
      }
    }

    contentNode.removeAttribute('inert')
  }, [hasOpenOverlay])

  const openTargetModal = (target: 'notifications' | 'price-alerts') => {
    if (target === 'notifications') {
      setIsNotificationOpen(true)
      setIsPriceAlertOpen(false)
      return
    }
    setIsPriceAlertOpen(true)
    setIsNotificationOpen(false)
  }

  const handleOpenTarget = (target: 'notifications' | 'price-alerts') => {
    if (authLoading) {
      return
    }
    if (authUser) {
      openTargetModal(target)
      return
    }
    setAuthTarget(target)
    setIsAuthDialogOpen(true)
  }

  const handleAuthenticated = (user: AuthUser) => {
    setAuthUser(user)
    if (authTarget) {
      openTargetModal(authTarget)
    }
    setIsAuthDialogOpen(false)
    setAuthTarget(null)
  }

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      <div
        ref={contentRef}
        aria-hidden={hasOpenOverlay}
        className={hasOpenOverlay ? 'pointer-events-none select-none' : ''}
      >
        <Header
          productCount={filteredCount}
          onOpenNotifications={() => handleOpenTarget('notifications')}
          onOpenPriceAlerts={() => handleOpenTarget('price-alerts')}
          systemStatus={systemStatus}
        />
        <Home
          onFilteredCountChange={setFilteredCount}
          onCategoriesChange={setCategories}
        />
      </div>
      <AuthDialog
        isOpen={isAuthDialogOpen}
        onClose={() => {
          setIsAuthDialogOpen(false)
          setAuthTarget(null)
        }}
        onAuthenticated={handleAuthenticated}
        target={authTarget}
      />
      <NotificationModal
        isOpen={isNotificationOpen}
        onClose={() => setIsNotificationOpen(false)}
        categories={categories}
        authUser={authUser}
        onAuthChange={setAuthUser}
      />
      <PriceAlertModal
        isOpen={isPriceAlertOpen}
        onClose={() => setIsPriceAlertOpen(false)}
        categories={categories}
        authUser={authUser}
        onAuthChange={setAuthUser}
      />
    </div>
  )
}

export default App
